import React, { useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import FontFamily from '@tiptap/extension-font-family';
import './TiptapEditor.css';
import CustomImage from "../components/CustomImage";

const TiptapEditor = ({ content, onChange, small = false }) => {
  const fileInputRef = useRef();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Image,
      TextStyle,
      Color,
      Highlight,
      FontFamily,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      CustomImage,
    ],
    content: content || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content, editor]);

  const insertImage = () => {
    fileInputRef.current.click();
  };

  // Upload file to backend instead of base64
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const token = localStorage.getItem("token"); // if protected
      const res = await fetch("https://digital.tifleducation.com/admin/public/api/upload-question-image", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) throw new Error("Image upload failed");

      const data = await res.json();
      if (data.url) {
        editor.chain().focus().setImage({ src: data.url }).run();
      } else {
        alert("Upload succeeded but no URL returned!");
      }
    } catch (err) {
      console.error("Image upload error:", err);
      alert("❌ Failed to upload image");
    }
  };

  return (
    <div className="tiptap-editor-wrapper">
      {editor && (
        <div className="tiptap-toolbar">
          {/* Font family */}
          <select
            onChange={(e) =>
              editor.chain().focus().setFontFamily(e.target.value).run()
            }
          >
            <option value="sans-serif">Sans Serif</option>
            <option value="serif">Serif</option>
            <option value="monospace">Monospace</option>
          </select>

          {/* Basic formatting */}
          <button onClick={() => editor.chain().focus().toggleBold().run()}>B</button>
          <button onClick={() => editor.chain().focus().toggleItalic().run()}>I</button>
          <button onClick={() => editor.chain().focus().toggleUnderline().run()}>U</button>
          <button onClick={() => editor.chain().focus().toggleHighlight().run()}>Highlight</button>

          {/* Text color */}
          <input
            type="color"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            title="Text Color"
            style={{ width: 30, height: 30 }}
          />

          {/* Headings */}
          <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</button>
          <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>

          {/* Alignment */}
          <button onClick={() => editor.chain().focus().setTextAlign('left').run()}>Left</button>
          <button onClick={() => editor.chain().focus().setTextAlign('center').run()}>Center</button>
          <button onClick={() => editor.chain().focus().setTextAlign('right').run()}>Right</button>

          {/* Links */}
          <button
            onClick={() => {
              const url = prompt('Enter link URL');
              if (url) {
                editor.chain().focus().setLink({ href: url }).run();
              }
            }}
          >
            🔗
          </button>

          {/* Images */}
          <button  onClick={insertImage}>🖼️</button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      )}
      <EditorContent editor={editor} className={small ? 'tiptap-small' : ''} />
    </div>
  );
};

export default TiptapEditor;
